import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Industry } from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getIndustries, getIndustryName } from '../../../../models/UiUtils';
import {
	useHandwrittenCardCategories,
	useHandwrittenCardCategoryTemplates,
	useHandwrittenCardSearchTemplates,
	useMyTemplatesQuery,
} from '../../../../queries';
import { KnownCategories } from '../../../../viewmodels/AppViewModels';
import { baseStyleSheet } from '../../../styles/styles';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Modal } from '../../Modal';
import { HandwrittenFoldedCardThumbnail } from '../HandwrittenfoldedCardThumbnail';
import { SearchField } from './components/SearchField';
import { styleSheet } from './styles';

const getIndustryOptions = (userAccountId: string, brokerDealer?: string) => {
	return getIndustries({ accountId: userAccountId, brokerDealer }).map(industry => ({
		value: industry,
		label: getIndustryName(industry),
	}));
};

const resourceSelectorCategoryMap: Partial<Record<Api.ResourceSelectorId, string>> = {
	[Api.ResourceSelectorId.HappyBirthday]: 'Birthday',
};

const SEARCH_DELAY = 600;
export const HandwrittenCardTemplateSearchModal = ({
	isOpen,
	onClose,
	onTemplateClicked,
	resourceSelectorId,
}: {
	isOpen: boolean;
	onClose: () => void;
	onTemplateClicked: (template: Api.IHandwrittenCardTemplate) => void;
	resourceSelectorId?: Api.ResourceSelectorId;
}) => {
	const userSession = useUserSession();

	const industryOptions = getIndustryOptions(userSession.account.id, userSession.account.additionalInfo?.brokerDealer);
	const [selectedIndustry, setSelectedIndustry] = React.useState<Industry>(industryOptions[0]?.value);
	const [searchValue, setSearchValue] = React.useState('');
	const [debouncedSearchValue, setDebouncedSearchValue] = React.useState(searchValue);
	React.useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedSearchValue(searchValue.trim());
		}, SEARCH_DELAY);
		return () => clearTimeout(timeout);
	}, [searchValue]);
	const searchTemplatesQuery = useHandwrittenCardSearchTemplates({
		enabled: !!debouncedSearchValue,
		industry: selectedIndustry,
		search: debouncedSearchValue,
	});
	const categoryQuery = useHandwrittenCardCategories({
		enabled: !!selectedIndustry,
		industry: selectedIndustry,
		withMyTemplates: true,
	});
	const categories: string[] = React.useMemo(() => {
		return categoryQuery.data ?? [];
	}, [categoryQuery.data]);

	const getDefaultCategory = (options: string[]) => {
		const categoryFromResourceSelector = options.find(x => x === resourceSelectorCategoryMap[resourceSelectorId]);
		return categoryFromResourceSelector ?? (options.length > 0 ? options[0] : null);
	};
	const [selectedCategory, setSelectedCategory] = React.useState<string>(getDefaultCategory(categories));
	// Track categories to reset selected category when options change
	const [prevCategories, setPrevCategories] = React.useState(categories);
	if (prevCategories !== categories) {
		setPrevCategories(categories);
		setSelectedCategory(getDefaultCategory(categories));
	}
	const cardTemplatesQuery = useHandwrittenCardCategoryTemplates({
		category: selectedCategory,
		enabled: !!selectedCategory && selectedCategory !== KnownCategories.MyTemplates,
		industry: selectedIndustry,
	});
	const myTemplatesQuery = useMyTemplatesQuery<Api.IHandwrittenCardTemplate>({
		enabled: selectedCategory === KnownCategories.MyTemplates,
		industry: selectedIndustry,
		templateType: Api.TemplateType.HandwrittenCard,
	});
	const isSearching = !!debouncedSearchValue;
	// Show loading status if either the search or category query is loading
	const isLoading =
		(isSearching && searchTemplatesQuery.isPending) ||
		(!isSearching && selectedCategory === KnownCategories.MyTemplates && myTemplatesQuery.isPending) ||
		(!isSearching && selectedCategory !== KnownCategories.MyTemplates && cardTemplatesQuery.isPending);
	const isEmpty =
		(isSearching && searchTemplatesQuery.status === 'success' && searchTemplatesQuery.data.length === 0) ||
		(!isSearching &&
			selectedCategory === KnownCategories.MyTemplates &&
			myTemplatesQuery.status === 'success' &&
			myTemplatesQuery.data.length === 0) ||
		(!isSearching &&
			selectedCategory !== KnownCategories.MyTemplates &&
			cardTemplatesQuery.status === 'success' &&
			cardTemplatesQuery.data.length === 0);
	let cardList: Api.IHandwrittenCardTemplate[] = [];
	if (isSearching && searchTemplatesQuery.status === 'success') {
		cardList = searchTemplatesQuery.data;
	} else if (
		!isSearching &&
		selectedCategory === KnownCategories.MyTemplates &&
		myTemplatesQuery.status === 'success'
	) {
		cardList = myTemplatesQuery.data;
	} else if (!isSearching && cardTemplatesQuery.status === 'success') {
		cardList = cardTemplatesQuery.data;
	}
	const handleChangeIndustry = (value: string) => {
		setSelectedIndustry(value as Industry);
		setSelectedCategory(null);
	};
	return (
		<Modal shouldCloseOnOverlayClick={false} useDefaultHeader isOpen={isOpen} onRequestClose={onClose}>
			<div className={`${css(styleSheet.container)} campaigns-by-category-selector`}>
				<header className={css(styleSheet.header)}>Select Card to Send</header>
				<SearchField
					onChange={ev => setSearchValue(ev.target.value)}
					onClickToClear={() => setSearchValue('')}
					placeholder='Search card type or keywords'
					value={searchValue}
				/>
				<div className={css(styleSheet.field)}>
					<Select value={selectedIndustry} onValueChange={handleChangeIndustry}>
						<SelectTrigger styles={styleSheet.trigger}>
							<span className={css(styleSheet.triggerLabel)}>Industry:</span>
							<span className={css(styleSheet.triggerText)}>
								{industryOptions.find(o => o.value === selectedIndustry)?.label}
							</span>
						</SelectTrigger>
						<SelectContent>
							{industryOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={css(styleSheet.field, debouncedSearchValue ? styleSheet.fieldDisabled : null)}>
					<Select
						value={selectedCategory ?? undefined}
						onValueChange={setSelectedCategory}
						disabled={!!debouncedSearchValue}
					>
						<SelectTrigger styles={styleSheet.trigger}>
							<span className={css(styleSheet.triggerLabel)}>Category:</span>
							<span className={css(styleSheet.triggerText)}>{selectedCategory}</span>
						</SelectTrigger>
						<SelectContent>
							{categories.map(category => (
								<SelectItem key={category} value={category}>
									{category}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				{isLoading ? (
					<div className={css(styleSheet.loadingContainer)}>
						<LoadingSpinner />
					</div>
				) : null}
				{isEmpty ? <p className={css(styleSheet.cardListEmpty)}>Oops... No cards were found.</p> : null}
				<div className={css(styleSheet.cardListContainer)}>
					{cardList.map(card => {
						return (
							<div key={card.id} className={css(styleSheet.cardListItem)}>
								{/* Replace with HandwrittenFoldedCardThumbnail for folded cards */}
								<HandwrittenFoldedCardThumbnail
									src={card.thumbnail?.publicUrl}
									alt={card.name}
									styleDeclaration={styleSheet.cardListItemImage}
								/>
								<h3 className={css(styleSheet.cardListItemTitle)}>{card.name}</h3>
								<footer className={css(styleSheet.cardListItemCta)}>
									<button className={css(baseStyleSheet.ctaButtonReverseSmall)} onClick={() => onTemplateClicked(card)}>
										Choose Card
									</button>
								</footer>
							</div>
						);
					})}
				</div>
			</div>
		</Modal>
	);
};
